import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../../components/ui/Button';
import { Loader } from '../../../components/ui/Loader';
import { Spinner } from '../../../components/ui/Spinner';
import {
  ArrowLeft,
  Book,
  ChevronDown,
  CreditCard,
  DollarSign,
  History,
  Info,
  MapPin,
  Package,
  Paperclip,
  Ruler,
  Weight,
} from 'lucide-react';
import Tooltip from '../../../components/ui/Tooltip';
import toast from 'react-hot-toast';
import { BookFilled, BookRegular } from '@fluentui/react-icons';
import { zoneMeaningMap } from '../../../constants/zoneMeaning';

export interface ServiceRate {
  serviceId: number;
  serviceName: string;
  displayName: string;
  tat: number;
  weight: number;
  volumetricWeight?: number;
  zone?: string;
  dimensions?: string;
  totalCharge: number;
  baseRate?: number;
  fuel?: number;
  gst?: number;
  otherCharges?: number;
  codCharge?: number;
  operationalStress?: boolean;
  baseWeightSlab: number;
}

interface OrderSummary {
  pickupState: string;
  pickupCity: string;
  deliveryCity: string;
  deliveryState: string;
  pickupPincode: string;
  deliveryPincode: string;
  invoiceValue: number;
  paymentMode: string;
  productDetails: string;
  quantity: number;
  weight: number;
  length: number;
  breadth: number;
  height: number;
  orderId: string;
}

export interface CourierRate {
  courierId: number;
  courierName: string;
  logoUrl: string;
  services: ServiceRate[];
}

interface RateSelectionProps {
  rates: CourierRate[];
  isLoading: boolean;
  isError: boolean;
  error?: any;
  orderSummary: OrderSummary;
  isSubmittingOrder?: boolean;
  submittingRateKey?: string | null;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (selectedRate: {
    courierId: number;
    courierName: string;
    serviceId: number;
    serviceName: string;
    totalCharge: number;
    tat: number;
  }) => void;
}

export const RateSelection: React.FC<RateSelectionProps> = ({
  rates,
  isLoading,
  isError,
  error,
  orderSummary,
  isSubmittingOrder = false,
  submittingRateKey = null,
  onBack,
  onClose,
  onConfirm,
}) => {
  const [selectedRate, setSelectedRate] = useState<any>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHandlingClick, setIsHandlingClick] = useState(false);

  const flatRates = useMemo(() => {
    const list = rates.flatMap((courier) =>
      courier.services.map((service) => ({
        courierId: courier.courierId,
        courierName: courier.courierName,
        logoUrl: courier.logoUrl,
        ...service,
        key: `${courier.courierId}-${service.serviceId}`,
      }))
    );
    return list.sort((a, b) => a.totalCharge - b.totalCharge);
  }, [rates]);

  useEffect(() => {
    if (flatRates.length > 0 && !selectedRate) {
      setSelectedRate(flatRates[0]);
    }
  }, [flatRates, selectedRate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-card">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-card py-4 pb-32 lg:pb-4">
      <div className="mx-auto px-4 lg:px-0">
        {/* Header */}
        <div className="mb-8 flex items-start gap-4">
          <div>
            <h1 className="text-2xl xl:text-3xl font-bold text-text">
              Select Shipping Option
            </h1>
            <p className="text-base text-text3">
              Choose the best shipping option for your order
            </p>
          </div>
        </div>

        {/* Error */}
        {isError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">
              {error?.message || 'Failed to load rates. Please try again.'}
            </p>
          </div>
        )}

        {/* GRID WRAPPER */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 items-start">
          {/* LEFT SIDE */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-sidebar rounded-xl border border-gray-200 shadow-sm p-6 lg:p-6 p-0 overflow-hidden">
              <div className="hidden lg:block">
                <h2 className="text-2xl font-bold text-white mb-1">
                  Available Shipping Options
                </h2>
                <p className="text-gray-200 text-sm">
                  Compare courier partners and choose the best rate
                </p>
              </div>

              {/* Mobile Routing Widget */}
              <div className="lg:hidden bg-gradient-to-br p-2 text-white">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex flex-col">
                    <span className="text-xl font-black tracking-tight">
                      {orderSummary.pickupPincode}
                    </span>
                    <span className="text-xs font-medium opacity-80 uppercase tracking-wide">
                      {orderSummary.pickupCity}
                    </span>
                  </div>

                  <div className="flex-1 px-4 flex items-center justify-center relative">
                    <div className="h-[1px] w-full border-t border-dashed border-white/40 absolute"></div>
                    <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center z-10 shadow-lg">
                      <div className="w-5 h-5 bg-teal rounded-full flex items-center justify-center">
                        <ChevronDown className="w-3 h-3 text-white transform -rotate-90 ml-0.5" />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <span className="text-xl font-black tracking-tight">
                      {orderSummary.deliveryPincode}
                    </span>
                    <span className="text-xs font-medium opacity-80 uppercase tracking-wide">
                      {orderSummary.deliveryCity}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Festive Peak Surcharge Info */}
            <div className="bg-red-50 border border-red-200 text-red-800 text-xs sm:text-sm px-4 py-3 rounded-xl flex items-start sm:items-center gap-3 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-red-500 shrink-0 mt-1.5 sm:mt-0"></span>
              <span className="font-medium">
                Delhivery Peak Surcharge: Surface ₹3.5 &amp; Air ₹5.5 per shipment due to festive season.
              </span>
            </div>

            {/* Desktop Header Row */}
            <div className="hidden lg:block bg-teal rounded-xl px-8 py-4 text-white font-semibold shadow-sm">
              <div className="grid grid-cols-7 gap-6 text-center">
                <div className="text-left">Courier Partner</div>
                <div>Delivery</div>
                <div>Weight</div>
                <div>Vol Weight</div>
                <div>Zone</div>
                <div>Charges</div>
              </div>
            </div>

            <div className="space-y-5 mt-5 max-h-[70vh] overflow-y-auto pr-2">
              {/* Mobile View */}
              <div className="lg:hidden space-y-3">
                {flatRates
                  .slice(0, isExpanded ? undefined : 4)
                  .map((rate: any) => (
                    <div
                      key={rate.key}
                      onClick={() => setSelectedRate(rate)}
                      className={`p-4 border rounded-xl flex flex-col gap-3 cursor-pointer transition-all ${selectedRate?.key === rate.key
                        ? 'border-teal bg-teal/5 ring-1 ring-teal'
                        : 'border-gray-200 bg-white'
                        }`}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg border bg-white flex items-center justify-center p-1 shrink-0">
                            <img
                              src={rate.logoUrl}
                              alt={rate.courierName}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-bold text-text text-sm">
                              {rate.courierName?.replace(/DELHIVERY\s*\(PE\)/gi, 'Delhivery').replace(/\(PE\)/gi, '').trim()}
                            </span>
                            <span className="text-[10px] text-text3 font-medium uppercase tracking-wider">
                              {rate.displayName?.replace(/by\s+/gi, '').replace(/\(PE\)/gi, '').trim()}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="text-lg font-bold text-teal">
                            ₹{rate.totalCharge.toLocaleString('en-IN')}
                          </div>
                          <div className="text-[10px] text-text3 font-medium">
                            Est. {rate.tat} Days
                          </div>
                        </div>
                      </div>

                    </div>
                  ))}

                {!isExpanded && flatRates.length > 4 && (
                  <button
                    onClick={() => setIsExpanded(true)}
                    className="w-full py-3 text-teal font-semibold flex items-center justify-center gap-2 border-2 border-dashed border-teal/20 rounded-xl mt-2 bg-teal/5 active:bg-teal/10 transition-colors"
                  >
                    <span>{flatRates.length - 4} more items</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Desktop List */}
              <div className="hidden lg:block space-y-5">
                {flatRates.length > 0 ? (
                  flatRates.map((rate) => (
                    <div
                      key={rate.key}
                      className="bg-white border border-gray-200 rounded-xl px-8 py-6 transition-all hover:border-teal hover:bg-teal/10"
                    >
                      <div className="grid grid-cols-7 gap-6 items-center text-center">
                        {/* Courier Info */}
                        <div className="flex items-center gap-4 text-left">
                          <div className="rounded-full bg-white border flex items-center justify-center shadow-sm">
                            <img
                              src={rate.logoUrl}
                              alt={rate.courierName}
                              className="w-20 object-contain rounded-full"
                            />
                          </div>

                          <div>
                            <div className="font-bold text-lg text-text">
                              {rate.courierName?.replace(/DELHIVERY\s*\(PE\)/gi, 'Delhivery').replace(/\(PE\)/gi, '').trim()}
                            </div>
                            <div className="text-sm text-teal font-semibold">
                              {rate.displayName?.replace(/by\s+/gi, '').replace(/\(PE\)/gi, '').trim()}
                            </div>
                            <div className="text-sm text-teal font-semibold whitespace-nowrap">
                              <span className="text-text3">
                                Slab: {rate.baseWeightSlab} Kg
                              </span>
                            </div>

                            <div className="text-sm text-text3 font-semibold camelcase">
                              RTO: ₹ {rate.totalCharge}
                            </div>
                            {rate.dimensions && (
                              <div className="text-sm text-text3">
                                Dim: {rate.dimensions}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Delivery */}
                        <div>
                          <div className="font-bold text-lg">
                            {rate.tat} Days
                          </div>
                          <div className="text-sm text-text3">
                            Estimated Delivery
                          </div>
                        </div>

                        {/* Chargeable Weight */}
                        <div>
                          <div className="font-bold text-lg">
                            {rate.weight} Kg
                          </div>
                          <div className="text-sm text-text3">Chargeable</div>
                        </div>

                        {/* Volumetric */}
                        <div>
                          <div className="font-bold text-lg">
                            {rate.volumetricWeight ?? '-'} Kg
                          </div>
                          <div className="text-sm text-text3">Volumetric</div>
                        </div>

                        {/* Zone */}
                        <div>
                          {(() => {
                            const rawZone = rate.zone ?? '-';
                            const normalizedZone = rawZone
                              .replace('-', ' ')
                              .toUpperCase();

                            return (
                              <>
                                <div className="font-bold lowercase text-lg">
                                  {normalizedZone}
                                </div>
                                <div className="text-sm text-text3">
                                  {zoneMeaningMap[normalizedZone] || ''}
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        {/* Charges + Button */}
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold text-teal">
                              ₹{rate.totalCharge.toLocaleString('en-IN')}
                            </div>

                            <Tooltip
                              variant="dark"
                              text={(() => {
                                let other = rate.otherCharges ?? 0;
                                let peak = 0;
                                if (rate.courierId === 3) {
                                  peak = rate.displayName?.toLowerCase().includes('air') ? 5.5 : 3.5;
                                  other = Number(Math.max(0, other - peak).toFixed(2));
                                }
                                let t = `Base: ₹${rate.baseRate ?? 0}`;
                                if (orderSummary.paymentMode?.toLowerCase() === 'cod') {
                                  t += `\nCOD Charge: ₹${rate.codCharge ?? 0}`;
                                }
                                t += `\nDPH: ₹${other}`;
                                if (peak > 0) t += `\nPeak Surcharge: ₹${peak}`;
                                t += `\nGST: ₹${rate.gst ?? 0}`;
                                return t;
                              })()}
                            />
                          </div>
                        </div>
                        <Button
                          onClick={async () => {
                            if (isHandlingClick || isSubmittingOrder) return;
                            setIsHandlingClick(true);
                            try {
                              await onConfirm({
                                courierId: rate.courierId,
                                courierName: rate.courierName,
                                serviceId: rate.serviceId,
                                serviceName: rate.serviceName,
                                totalCharge: rate.totalCharge,
                                tat: rate.tat,
                              });
                            } catch (err: any) {
                              toast.error(
                                err?.message || 'Failed to create order'
                              );
                              setIsHandlingClick(false);
                            }
                          }}
                          disabled={isSubmittingOrder || isHandlingClick}
                          className="bg-teal hover:bg-teal-dark text-white px-6 py-2 rounded-lg font-semibold inline-flex items-center justify-center gap-2 min-w-[120px] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {isSubmittingOrder &&
                            submittingRateKey === rate.key ? (
                            <>
                              <Spinner size="sm" className="shrink-0" />
                              Creating order...
                            </>
                          ) : (
                            'Ship Now'
                          )}
                        </Button>
                      </div>


                      {/* Operational Stress */}
                      {rate.operationalStress && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-md flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-amber-500"></span>
                          This courier is facing operational stress on this
                          lane. Order might get delayed.
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="bg-white border rounded-xl py-12 text-center text-text3">
                    No rates available. Please check your order details.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden sticky top-8">
              <div className="bg-sidebar px-6 py-5">
                <h2 className="text-xl font-bold text-white">Order Summary</h2>
                <p className="text-sm text-gray-200 mt-1">Shipment overview</p>
              </div>

              <div className="p-4 space-y-6">
                {/* Route Details */}
                <div className="hidden lg:block space-y-4">
                  <div className="text-sm tracking-wide text-text3 font-semibold">
                    Route Details
                  </div>
                  <div className="flex flex-col space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <MapPin className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Pickup</p>
                        <p className="font-semibold text-text">
                          {orderSummary.pickupPincode},
                          <span> {orderSummary.pickupState},</span>
                          <span> {orderSummary.pickupCity}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-50 rounded-lg">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Delivery</p>
                        <p className="font-semibold text-text">
                          {orderSummary.deliveryPincode},
                          <span> {orderSummary.deliveryState},</span>
                          <span> {orderSummary.deliveryCity}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-50 rounded-lg">
                        <BookRegular className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Customer History</p>
                        <p className="font-semibold text-text">N/A</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="pt-4 border-t space-y-4">
                  <div className="text-sm tracking-wide text-text3 font-semibold">
                    Payment Details
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <Paperclip className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Order ID</p>
                        <p className="font-semibold text-text uppercase">
                          {orderSummary.orderId}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Order Value</p>
                        <p className="font-semibold text-text">
                          ₹ {orderSummary.invoiceValue.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <CreditCard className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-text3">Payment Mode</p>
                        <p className="font-semibold text-text">
                          {orderSummary.paymentMode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Details */}
                <div className="pt-4 border-t space-y-4">
                  <div className="text-sm tracking-wide text-text3 font-semibold">
                    Package Details
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <Info className="w-4 h-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-text3">Product Details</p>
                      <p className="font-semibold text-text">
                        {orderSummary.productDetails}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <Package className="w-4 h-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-text3">Quantity</p>
                      <p className="font-semibold text-text">
                        {orderSummary.quantity} Item
                        {orderSummary.quantity > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Weight className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-text3">Weight</p>
                      <p className="font-semibold text-text">
                        {orderSummary.weight} Kg
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-50 rounded-lg">
                      <Ruler className="w-4 h-4 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-sm text-text3">Dimensions</p>
                      <p className="font-semibold text-text">
                        {orderSummary.length} × {orderSummary.breadth} ×{' '}
                        {orderSummary.height}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 w-full flex justify-center">
                    <Button
                      onClick={onClose}
                      variant={'outline'}
                      className="text-sm p-2"
                    >
                      View All Details
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile Footer */}
      <div className="lg:hidden sticky bottom-0 left-0 right-0  pb-0 pt-1 z-50">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)] p-4 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-text3 uppercase tracking-wider">
              Total Payable
            </span>
            <div className="text-2xl font-black text-teal">
              ₹ {selectedRate?.totalCharge.toLocaleString('en-IN') || 0}
            </div>
            {selectedRate && (
              <span className="text-[10px] text-text3 font-medium">
                {selectedRate.courierName} • {selectedRate.tat} Days
              </span>
            )}
          </div>
          <Button
            disabled={!selectedRate || isSubmittingOrder || isHandlingClick}
            onClick={async () => {
              if (selectedRate) {
                if (isHandlingClick || isSubmittingOrder) return;
                setIsHandlingClick(true);
                try {
                  await onConfirm({
                    courierId: selectedRate.courierId,
                    courierName: selectedRate.courierName,
                    serviceId: selectedRate.serviceId,
                    serviceName: selectedRate.serviceName,
                    totalCharge: selectedRate.totalCharge,
                    tat: selectedRate.tat,
                  });
                } catch (err: any) {
                  toast.error(err?.message || 'Failed to create order');
                  setIsHandlingClick(false);
                }
              }
            }}
            className="bg-teal hover:bg-teal-dark text-white px-8 h-12 rounded-xl font-bold flex items-center justify-center gap-2 min-w-[140px] shadow-lg shadow-teal/20"
          >
            {isSubmittingOrder ? (
              <>
                <Spinner size="sm" />
                <span>Shipping...</span>
              </>
            ) : (
              'Ship Now'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
